import {TargetBlank} from '@wandb/weave/common/util/links';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {memo} from 'react';

import DOC_URLS from '../util/doc_urls';
import {SearchableJSONTree} from './SearchableJSONTree/SearchableJSONTree';
import {
  FilterJSONOptions,
  isJSONNode,
  JSONNode,
  JSONValue,
  RecordStringUnknown,
} from './SearchableJSONTree/util/filterJSON';

interface RunConfigTableProps {
  config: unknown; // Receives raw config (json object)

  // used for copy analytics
  projectName: string;
  entityName: string;
  userID: string;
}

export const RunConfigTree: React.FC<RunConfigTableProps> = memo(
  ({config, projectName, entityName, userID}) => {
    if (!isValidRunConfig(config)) {
      return <EmptyRunConfigMessage />;
    }

    return (
      <SearchableJSONTree
        json={config}
        rootKeyLabel="Config parameters"
        filterOptions={defaultConfigNodeFilterOptions}
        eventData={{
          projectName,
          entityName,
          userID,
        }}
      />
    );
  }
);
RunConfigTree.displayName = 'RunConfigTree';

const EmptyRunConfigMessage = () => {
  return (
    <Tailwind>
      <div className="TailwindSegment">
        <div className="TailwindSegmentHeader">
          No config parameters were saved for this run.
        </div>
        <p>
          Check the{' '}
          <TargetBlank className="TailwindLink" href={DOC_URLS.config}>
            configuration documentation
          </TargetBlank>{' '}
          for more information.
        </p>
      </div>
    </Tailwind>
  );
};

interface RunConfigNode extends RecordStringUnknown {
  desc: string | null;
  value: JSONNode | JSONValue;
}

const isValidRunConfig = (
  config: unknown
): config is Record<string, unknown> => {
  if (!isJSONNode(config)) {
    return false;
  }

  const configKeys = Object.keys(config);
  // If the _wandb key is the only key, it's not a valid config
  if (configKeys.length === 1 && configKeys.includes('_wandb')) {
    return false;
  }
  return true;
};

// wandb config values generally look like {desc: optionalDescription, value: actualValue}. this function checks for that data shape.
const isRunConfigNode = (node: JSONNode): node is RunConfigNode => {
  return (
    node != null &&
    typeof node === 'object' &&
    ((Object.keys(node).length === 2 &&
      // eslint-disable-next-line no-prototype-builtins
      node.hasOwnProperty('desc') &&
      // eslint-disable-next-line no-prototype-builtins
      node.hasOwnProperty('value')) ||
      // Some configs only have a 'value' field, no 'desc'
      // eslint-disable-next-line no-prototype-builtins
      (Object.keys(node).length === 1 && node.hasOwnProperty('value')))
  );
};

const isRunConfigDescriptionEmpty = (
  node: RunConfigNode
): node is RunConfigNode => {
  return node.desc == null || node.desc.trim() === '';
};

// wandb config values look like {desc: optionalDescription, value: actualValue}.
// this function checks for that data shape and, if the desc key is empty, just returns the value.
const stripRunConfigNodeDescription = (
  node: JSONNode
): JSONNode | JSONValue => {
  if (isRunConfigNode(node) && isRunConfigDescriptionEmpty(node)) {
    return node.value;
  }
  return node;
};

export const defaultConfigNodeFilterOptions: FilterJSONOptions = {
  excludeList: ['_wandb'],
  nodeTransformFunction: stripRunConfigNodeDescription,
};
